import {
  type ClassDef,
  type CombatState,
  AbilitySlot,
  castAbility,
  updateCombatState,
  createCombatState,
  type AbilityCastResult,
} from "@craftgaming/shared";

const SLOT_KEYS = ["Digit1", "Digit2", "Digit3", "Digit4"];
const SLOT_LABELS = ["1", "2", "3", "4"];

export type AbilityBar = {
  state: CombatState;
  update: (dt: number) => void;
  getLastCast: () => AbilityCastResult | null;
  setLookDirection: (dir: { x: number; y: number; z: number }) => void;
  setPosition: (pos: { x: number; y: number; z: number }) => void;
  dispose: () => void;
};

export function createAbilityBar(classDef: ClassDef): AbilityBar {
  const combatState = createCombatState(classDef);
  let lastCast: AbilityCastResult | null = null;
  let lookDir = { x: 0, y: 0, z: -1 };
  let position = { x: 0, y: 0, z: 0 };

  // Create DOM — positioned above status bars
  const container = document.createElement("div");
  container.id = "ability-bar";
  container.style.cssText = `
    position: absolute;
    bottom: 100px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 4px;
    pointer-events: auto;
    user-select: none;
  `;

  // Class name + mana display
  const infoRow = document.createElement("div");
  infoRow.style.cssText = `
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2px;
    padding: 0 4px;
  `;

  const classLabel = document.createElement("span");
  classLabel.style.cssText = `
    font-size: 10px;
    color: #aaa;
    font-family: monospace;
    white-space: nowrap;
  `;
  classLabel.textContent = classDef.name;
  infoRow.appendChild(classLabel);

  const manaLabel = document.createElement("span");
  manaLabel.style.cssText = `
    font-size: 10px;
    color: #44aaff;
    font-family: monospace;
  `;
  infoRow.appendChild(manaLabel);

  container.appendChild(infoRow);

  // Mana bar (thin, under abilities)
  const manaContainer = document.createElement("div");
  manaContainer.style.cssText = `
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: 3px;
    background: #1a1a2e;
    border-radius: 2px;
    overflow: hidden;
    margin-top: 2px;
  `;
  const manaFill = document.createElement("div");
  manaFill.style.cssText = `
    height: 100%;
    background: linear-gradient(90deg, #2244ff, #44aaff);
    transition: width 0.1s;
    width: 100%;
    border-radius: 2px;
  `;
  manaContainer.appendChild(manaFill);
  container.appendChild(manaContainer);

  // Ability slots
  const slotElements: HTMLDivElement[] = [];
  const cooldownOverlays: HTMLDivElement[] = [];
  const cooldownTexts: HTMLSpanElement[] = [];

  for (let i = 0; i < 4; i++) {
    const ability = classDef.abilities[i];

    const slot = document.createElement("div");
    slot.style.cssText = `
      width: 44px;
      height: 44px;
      border: 2px solid ${ability.color}44;
      background: #1a1a2e;
      border-radius: 6px;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    `;
    slot.title = `${ability.name}: ${ability.description}`;

    // Ability icon (colored circle)
    const icon = document.createElement("div");
    icon.style.cssText = `
      width: 28px;
      height: 28px;
      background: radial-gradient(circle, ${ability.color}, ${ability.color}44);
      border-radius: 50%;
      opacity: 0.9;
    `;
    slot.appendChild(icon);

    // Key label
    const keyLabel = document.createElement("span");
    keyLabel.textContent = SLOT_LABELS[i];
    keyLabel.style.cssText = `
      position: absolute;
      bottom: 1px;
      right: 3px;
      font-size: 9px;
      font-weight: bold;
      color: #fff;
      font-family: monospace;
      text-shadow: 0 1px 2px rgba(0,0,0,0.8);
    `;
    slot.appendChild(keyLabel);

    // Cooldown overlay
    const cdOverlay = document.createElement("div");
    cdOverlay.style.cssText = `
      position: absolute;
      inset: 0;
      background: rgba(0,0,0,0.7);
      border-radius: 6px;
      display: none;
      align-items: center;
      justify-content: center;
    `;
    const cdText = document.createElement("span");
    cdText.style.cssText = `
      color: #fff;
      font-size: 13px;
      font-weight: bold;
      font-family: monospace;
    `;
    cdOverlay.appendChild(cdText);
    slot.appendChild(cdOverlay);

    container.appendChild(slot);
    slotElements.push(slot);
    cooldownOverlays.push(cdOverlay);
    cooldownTexts.push(cdText);
  }

  const overlay = document.getElementById("ui-overlay");
  if (overlay) overlay.appendChild(container);

  // Input handler
  function handleKeyDown(e: KeyboardEvent) {
    const slotIdx = SLOT_KEYS.indexOf(e.code);
    if (slotIdx === -1) return;

    const result = castAbility(combatState, slotIdx as AbilitySlot, position, lookDir);
    if (result.success) {
      lastCast = result;
      slotElements[slotIdx].style.transform = "scale(1.15)";
      slotElements[slotIdx].style.borderColor = classDef.abilities[slotIdx].color;
      setTimeout(() => {
        slotElements[slotIdx].style.transform = "scale(1)";
        slotElements[slotIdx].style.borderColor = `${classDef.abilities[slotIdx].color}44`;
      }, 150);
    }
  }

  document.addEventListener("keydown", handleKeyDown);

  function update(dt: number) {
    updateCombatState(combatState, dt);

    // Mana bar + label
    const manaPercent = (combatState.mana / combatState.maxMana) * 100;
    manaFill.style.width = `${manaPercent}%`;
    manaLabel.textContent = `${Math.floor(combatState.mana)}/${combatState.maxMana}`;

    // Cooldown overlays
    for (let i = 0; i < 4; i++) {
      const abilityState = combatState.abilities[i];
      if (abilityState.cooldownRemaining > 0) {
        cooldownOverlays[i].style.display = "flex";
        cooldownTexts[i].textContent = Math.ceil(abilityState.cooldownRemaining).toString();
      } else {
        cooldownOverlays[i].style.display = "none";
      }
    }
  }

  function dispose() {
    document.removeEventListener("keydown", handleKeyDown);
    container.remove();
  }

  return {
    state: combatState,
    update,
    getLastCast() {
      const cast = lastCast;
      lastCast = null;
      return cast;
    },
    setLookDirection(dir) { lookDir = dir; },
    setPosition(pos) { position = pos; },
    dispose,
  };
}
